import { TheWestSection } from '@news-mono/common'
import {
    calcRem,
    metrics,
    theWestTextOnlySectionHeaderOverride,
} from '@news-mono/component-library'
import {
    ElectionDefinition,
    getElectionAdPageKindFromElectionId,
    getElectionToggles,
    GetRouteAdTargeting,
    getSocialImageMeta,
    PageType,
    TogglesReduxState,
} from '@news-mono/web-common'
import { layout } from '../../../App.routing'
import { billboardLeaderboardMrec } from '../../ad-units'
import { getYear } from 'date-fns'

type CreateElectionPageArgs = {
    getAdTargeting: GetRouteAdTargeting
    electionDefinition: ElectionDefinition
    toggles: TogglesReduxState
}

export const createElectionLandingPage = ({
    getAdTargeting,
    electionDefinition,
    toggles,
}: CreateElectionPageArgs): PageType<TheWestSection> => {
    const {
        'election-landing-page-the-race': isTheRaceEnabled,
        'election-landing-page-the-seats': isTheSeatsEnabled,
        'election-section-head-to-head': isHeadToHeadEnabled,
        'election-landing-page-map': isMapEnabled,
    } = getElectionToggles(electionDefinition, toggles)

    const isFederal =
        electionDefinition.electionData?.config?.electionType.toLowerCase() ===
        'federal'
    const electionYear = getYear(
        new Date(electionDefinition.electionData?.config?.electionDate ?? ''),
    )
    const electionState = electionDefinition.electionData?.config?.state
    const electionPageKind = getElectionAdPageKindFromElectionId(
        electionDefinition.electionId,
    )

    const meta = electionDefinition.meta

    let defaultTitle
    let collectionHeading

    if (isFederal) {
        defaultTitle = `${electionYear} Federal Election`
        collectionHeading = 'Latest Federal Election News'
    } else {
        defaultTitle = `${electionYear} ${electionState} Election`
        collectionHeading = `Latest ${electionState} Election News`
    }

    // Prioritise topic metadata over page-specific metadata.
    const seoTitle =
        meta.topic?.seoTitle ?? meta.updates?.seoTitle ?? defaultTitle
    const seoDescription =
        meta.topic?.seoDescription ?? meta.updates?.seoDescription

    const adTargeting = getAdTargeting(
        electionPageKind ?? 'home',
        'default',
        'home',
    )

    // as per https://swmdigital-wa.slack.com/archives/C07TZDGREDA/p1741412941432569?thread_ts=1741411065.813789&cid=C07TZDGREDA
    // not sure if this is the best way to do it, so we should fix it up later
    adTargeting.ssAdUnits = adTargeting.ssAdUnits
        .flatMap((adUnit) => adUnit.split('/'))
        .map((adUnit) =>
            adUnit === 'WAelection2025' ? 'WA election 2025' : adUnit,
        )

    return {
        kind: 'page',
        heading: seoTitle,
        pageType: 'static',
        section: 'default',
        hideHeading: true,
        adTargeting,
        pageMeta: {
            title: seoTitle,
            description: seoDescription,
            link: meta.canonical
                ? [
                      {
                          rel: 'canonical',
                          href: `${meta.canonical}politics/${electionDefinition.electionId}`,
                      },
                  ]
                : undefined,
            meta: meta.updates?.socialImage
                ? getSocialImageMeta(meta.updates.socialImage, '2400', '1260')
                : undefined,
        },
        socialMeta: {
            title: seoTitle,
            description: seoDescription,
        },
        compositions: [
            layout.composition({
                type: 'box',
                props: {
                    containerWidth:
                        metrics.thewest.siteMetrics.mainContentWidth,
                    flex: {
                        default: {
                            flexDirection: 'column',
                            gap: calcRem(24),
                        },
                        sm: {
                            gap: calcRem(48),
                        },
                    },
                    verticalGutters: ['unset', 'xxl'],
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {},
                            contentAreas: {
                                main: [
                                    layout.component({
                                        type: 'breaking-news',
                                        props: {
                                            dataDefinitionArgs: {
                                                type: 'curation',
                                                name: 'breaking-news',
                                                offset: 0,
                                                pageSize: 1,
                                            },
                                            isElectionPageRoute: true,
                                            removeBottomMargin: true,
                                        },
                                    }),
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            flex: {
                                                default: {
                                                    flexDirection: 'column',
                                                    gap: calcRem(0), // Set no gap between breaking news and election navigation
                                                },
                                            },
                                            verticalSpacing: 'unset',
                                            breakpoints: {
                                                lg: {
                                                    horizontalGuttersOverride: 15,
                                                },
                                            },
                                        },
                                        contentAreas: {
                                            main: [
                                                ...(isHeadToHeadEnabled
                                                    ? [
                                                          layout.component({
                                                              type: 'election-head-to-head-seat-count-widget',
                                                              props: {
                                                                  electionDefinition,
                                                              },
                                                          }),
                                                      ]
                                                    : []),
                                                layout.component({
                                                    type: 'election-navigation',
                                                    props: {
                                                        electionDefinition,
                                                        activePage: 'updates',
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),

                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                horizontalSpacing: 'outerMargin',
                                flex: {
                                    default: {
                                        flexDirection: 'column',
                                        gap: calcRem(24),
                                    },
                                    lg: {
                                        gap: calcRem(48),
                                    },
                                },
                            },
                            contentAreas: {
                                main: [
                                    layout.nestedComposition({
                                        type: 'component-overrider',
                                        props: {
                                            override: () =>
                                                theWestTextOnlySectionHeaderOverride,
                                        },
                                        contentAreas: {
                                            children: [
                                                layout.component({
                                                    type: 'sierra',
                                                    props: {
                                                        cardLayout: [
                                                            'the-west-hero',
                                                            {
                                                                type: 'november',
                                                                listLength: 4,
                                                                hasBackground:
                                                                    false, //So that the border appears on the last item ¯\_(ツ)_/¯
                                                                paddingOverride:
                                                                    calcRem(
                                                                        16,
                                                                        0,
                                                                        0,
                                                                        0,
                                                                    ),
                                                            },
                                                        ],
                                                        isLarge: true,
                                                        heroBottomPadding: 8,
                                                        dataDefinitionArgs: {
                                                            type: 'curation',
                                                            // !! Warning !! Election curations will need to be created for every future election, or the page will resolve to 'Not Found'.
                                                            name: electionDefinition.electionId,
                                                            pageSize: 5,
                                                            offset: 0,
                                                        },
                                                        gridGap: 24,
                                                        breakpoints: {
                                                            lg: {
                                                                gridGap: 32,
                                                            },
                                                        },
                                                        sectionHeader: {
                                                            heading: 'Updates',
                                                            isStandalonePage:
                                                                true,
                                                        },
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                    ...(isTheRaceEnabled
                                        ? [
                                              layout.component({
                                                  type: 'election-the-race-widget',
                                                  props: {
                                                      electionDefinition,
                                                  },
                                              }),
                                          ]
                                        : []),
                                    ...(isTheSeatsEnabled
                                        ? [
                                              layout.component({
                                                  type: 'election-seat-widget',
                                                  props: {
                                                      electionDefinition,
                                                      electoratePageUrl: `/politics/${electionDefinition.electionId}/electorates`,
                                                      titleText:
                                                          'Electorate Results',
                                                  },
                                              }),
                                          ]
                                        : []),
                                    ...(isMapEnabled
                                        ? [
                                              layout.component({
                                                  type: 'election-map-overview-widget',
                                                  props: {
                                                      titleText: 'Map Overview',
                                                      electionDefinition,
                                                  },
                                              }),
                                          ]
                                        : []),
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {},
                                        contentAreas: {
                                            main: billboardLeaderboardMrec(
                                                'one',
                                                0,
                                            ),
                                        },
                                    }),
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {},
                                        contentAreas: {
                                            main: [
                                                layout.nestedComposition({
                                                    type: 'component-overrider',
                                                    props: {
                                                        override: () =>
                                                            theWestTextOnlySectionHeaderOverride,
                                                    },
                                                    contentAreas: {
                                                        children: [
                                                            layout.component({
                                                                type: 'uniform',
                                                                props: {
                                                                    cardLayout:
                                                                        [
                                                                            {
                                                                                type: 'november',
                                                                                listLength: 5,
                                                                            },
                                                                            [
                                                                                'large-card',
                                                                            ],
                                                                            [
                                                                                'large-card-mdNoImage',
                                                                                'large-card-mdNoImage',
                                                                            ],
                                                                            [
                                                                                'large-card-mdNoImage',
                                                                                'large-card-mdNoImage',
                                                                            ],
                                                                        ],
                                                                    section:
                                                                        'default',
                                                                    sectionHeader:
                                                                        {
                                                                            heading:
                                                                                collectionHeading,
                                                                        },
                                                                    dataDefinitionArgs:
                                                                        {
                                                                            type: 'listing',
                                                                            topics: [
                                                                                `politics/${electionDefinition.electionId}`,
                                                                            ],
                                                                            includeSubTopics:
                                                                                true,
                                                                            publicationKind:
                                                                                'article',
                                                                            paging: {
                                                                                page: 1,
                                                                                pageSize: 10,
                                                                            },
                                                                        },
                                                                    hasBorder:
                                                                        false,
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                                layout.component({
                                                    type: 'lima',
                                                    props: {
                                                        verticalSpacing: 'md',
                                                        fontScale: 1.2,
                                                        fixedRatios: ['16:9'],
                                                        hasBackground: true,
                                                        initialColumns: 1,
                                                        intermediateColumns: 2,
                                                        finalColumns: 4,

                                                        loadMoreEnabled: true,
                                                        dataDefinitionArgs: {
                                                            type: 'listing',
                                                            topics: [
                                                                `politics/${electionDefinition.electionId}`,
                                                            ],
                                                            includeSubTopics:
                                                                true,
                                                            publicationKind:
                                                                'article',
                                                            paging: {
                                                                page: 1,
                                                                pageOffset: 10,
                                                                pageSize: 8,
                                                            },
                                                        },
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {},
                                        contentAreas: {
                                            main: billboardLeaderboardMrec(
                                                'two',
                                                0,
                                            ),
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ],
    }
}
